import { FC, HTMLAttributes, useState } from "react";

import { Button, Col, Form, Input, Popover, Row, Space, Spin } from "antd";
import { clsx } from "clsx";

import { SvgIcon } from "../../../../common/components/SvgIcon";
import { handleError } from "../../../../common/ErrorHandling";
import { RawFeedback } from "../../../../types/ReportTypes";
import classes from "./Feedback.module.less";

interface FeedbackProps extends HTMLAttributes<HTMLDivElement> {
    like?: boolean;
    onFeedbackAdd?: (feedback: RawFeedback) => Promise<void>;
    onFeedbackRemove?: () => Promise<void>;
}

type FormValues = {
    content: string;
};

export const Feedback: FC<FeedbackProps> = ({
    like,
    onFeedbackAdd,
    onFeedbackRemove,
    className,
    ...props
}) => {
    const [form] = Form.useForm<FormValues>();
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleThumbsUp = async () => {
        setLoading(true);
        try {
            if (like === true) {
                await onFeedbackRemove?.();
            } else {
                if (like === false) {
                    await onFeedbackRemove?.();
                }
                await onFeedbackAdd?.({ isPositive: true });
            }
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleThumbsDown = async () => {
        setLoading(true);
        try {
            if (like === false) {
                await onFeedbackRemove?.();
            } else {
                setShowConfirm(true);
            }
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (value: FormValues) => {
        setLoading(true);
        try {
            if (like === true) {
                await onFeedbackRemove?.();
            }
            await onFeedbackAdd?.({ isPositive: false, comment: value.content });
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const closeAndResetPopover = () => {
        setShowConfirm(false);
        form.resetFields();
    };

    const content =
        like === false ? (
            <>
                <Row>
                    <Col flex={1}></Col>
                    <Col>
                        <SvgIcon
                            name="close"
                            size={12}
                            color="#1890FF"
                            className={classes.close}
                            onClick={closeAndResetPopover}
                        />
                    </Col>
                </Row>
                <div className={classes.title}>Thank you</div>
                <div className={classes.subtitle}>Your feedback has been received.</div>
                <div className={classes.footer}>
                    <Button type="primary" onClick={closeAndResetPopover}>
                        Got it!
                    </Button>
                </div>
            </>
        ) : (
            <>
                <Row>
                    <Col flex={1} className={classes.header}>
                        Tell us why...
                    </Col>
                    <Col>
                        <SvgIcon
                            name="close"
                            size={12}
                            color="#1890FF"
                            onClick={() => setShowConfirm(false)}
                            className={classes.close}
                        />
                    </Col>
                </Row>
                <Form form={form} onFinish={handleSubmit} size="small" disabled={loading}>
                    <Form.Item
                        name={["content"]}
                        rules={[
                            { required: true, message: "Please enter your feedback" },
                            {
                                type: "string",
                                max: 10_000,
                                message: "Maximum 10,000 characters",
                            },
                        ]}
                    >
                        <Input.TextArea
                            bordered={false}
                            rows={3}
                            className={classes.input}
                        ></Input.TextArea>
                    </Form.Item>
                    <Form.Item>
                        <Row justify="end">
                            <Space size={16}>
                                <Button
                                    onClick={() => setShowConfirm(false)}
                                    className={classes.cancel}
                                >
                                    It’s ok
                                </Button>
                                <Button type="primary" htmlType="submit" loading={loading}>
                                    Submit
                                </Button>
                            </Space>
                        </Row>
                    </Form.Item>
                </Form>
            </>
        );

    return (
        <div {...props} className={clsx(classes.container, className)}>
            <Spin spinning={loading} delay={250}>
                <SvgIcon
                    name="thumbsup"
                    size={18}
                    className={clsx(classes.like, like === true && classes.active)}
                    onClick={handleThumbsUp}
                />
                <Popover
                    content={<div className={classes.content}>{content}</div>}
                    trigger={["click"]}
                    open={showConfirm}
                    showArrow={false}
                    onOpenChange={(open) => {
                        if (!open) {
                            setShowConfirm(open);
                            if (like === false) form.resetFields();
                        }
                    }}
                >
                    <SvgIcon
                        name="thumbsdown"
                        size={18}
                        className={clsx(classes.dislike, like === false && classes.active)}
                        onClick={handleThumbsDown}
                    />
                </Popover>
            </Spin>
        </div>
    );
};
